'use client';
import { useState, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Modal, ModalBody, ModalFooter } from '../../components/ui/Modal';
import { RowPickerTrigger } from '../../components/ui/RowPicker';
import { SettingsRow } from '../../components/ui/SettingsSurface';
import { useTranslation } from '../../lib/i18n';
import { saveHalted } from '../../lib/useAutoSaveStatus';
import type { PluginConfigDraft } from '../../lib/usePluginConfigDraft';

/** The record a document or structured-list field wears: a summary trigger in the control cell, and the
 *  real editor in a modal raised from it. The trigger is named after the FIELD, not after what is stored,
 *  so a card full of them still reads as a list of settings to a screen reader. */
export function ModalFieldRow({ label, description, hint, status, summary, fillsModal, saveState, children }: {
  label: string;
  description?: string;
  hint?: string;
  status?: ReactNode;
  summary: string;
  /** Parent-owned persistence: the parent reports its outcome as a toast, in the surface it owns. */
  saveState?: Pick<PluginConfigDraft, 'status' | 'flush' | 'owner'>;
  /** A Monaco surface takes the modal's whole height; a textarea or an entry list scrolls in the body. */
  fillsModal?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // A halted save keeps the editor open with its draft: closing would walk away from a value that was not
  // stored. Its recovery card is raised inside this dialog (`Modal.reportedBy`), so retry and reload are reachable
  // from here, and Done works again once the owner's state says the failure is over.
  const closeDisabled = saveState?.status === 'saving' || saveHalted(saveState?.status);
  const close = async () => {
    const finalStatus = await saveState?.flush();
    if (!saveHalted(finalStatus)) setOpen(false);
  };
  return (
    <>
      <SettingsRow
        label={label}
        description={description}
        hint={hint}
        status={status}
        control={
          <RowPickerTrigger
            label={label}
            summary={summary}
            trailingIcon={Pencil}
            aria-expanded={open}
            onClick={() => setOpen(true)}
          />
        }
      />
      {open ? (
        <Modal title={label} description={description} size="lg" onClose={close} closeDisabled={closeDisabled} reportedBy={saveState?.owner}>
          {fillsModal ? <div className="min-h-0 flex-1 overflow-hidden">{children}</div> : <ModalBody>{children}</ModalBody>}
          <ModalFooter>
            <Button variant="accent" onClick={close} disabled={closeDisabled}>{t.common.done}</Button>
          </ModalFooter>
        </Modal>
      ) : null}
    </>
  );
}
